/**
 * Sidebar — shared sidebar component with activity bar and panel content.
 *
 * Supports all sidebar views from the store. Each host app can use it directly
 * since all panel components are imported from editor-core.
 */

import { useT } from "@magic-md/i18n/react";
import {
    ExternalLink,
    FileText,
    GripHorizontal,
    Maximize2,
    PanelLeft,
    PinOff,
    Puzzle,
    Settings,
    X,
} from "lucide-react";
import type { ReactNode } from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../lib/utils";
import type { WorkspacePinnedUnit } from "../lib/pinned-units";
import {
    getSidebarPresentation,
    type SidebarView,
    useEditorStore,
} from "../store/editor-store";
import { getPlatformService } from "../platform";
import { ExtensionSidebarPanelView } from "../extensions/ExtensionContributions";
import {
    useExtensionSidebarPanels,
    useExtensionUIProps,
} from "../extensions/useExtensionContributions";
import {
    useExtensionHostStore,
    type FloatingExtensionSidebarPanel,
} from "../extensions/extension-host-store";
import type { ProductIconSlotId } from "../theme/registry/icon-slot-registry";
import { ProductIcon } from "../theme/runtime/theme-icons";
import { buildExtensionContextMenuItems } from "../extensions/extension-menu-bridge";
import { renderExtensionActivityIcon } from "../extensions/extension-activity-icon";
import { pluginUIRegistry } from "../plugins/plugin-ui-registry";
import { ContextMenu, type ContextMenuItem, useContextMenu } from "./ContextMenu";
import { buildTraceRecordingMenuItem } from "./context-menu-builders";
import { EnginePanel } from "./EnginePanel";
import { ExplorerPanel } from "./ExplorerPanel";
import { OpenFilesSection } from "./OpenFilesSection";
import { PinnedUnitPanel } from "./PinnedUnitPanel";
import { PublishPanel } from "./PublishPanel";
import { SearchPanel } from "./SearchPanel";
import { SidebarCloseButton } from "./SidebarCloseButton";
import { TocPanel } from "./TocPanel";
import { SnapshotPreviewColumn } from "./SnapshotPreviewColumn";

/* ── Constants ── */

const MIN_PANEL_WIDTH = 200;
const MAX_PANEL_WIDTH = 600;
const DEFAULT_PANEL_WIDTH = 320;
const FLOATING_PANEL_MIN_WIDTH = 280;
const FLOATING_PANEL_MIN_HEIGHT = 240;
const FLOATING_PANEL_MAX_WIDTH = 720;
const FLOATING_PANEL_MAX_HEIGHT = 820;
type ActiveExtensionPanel = { extensionId: string; panelId: string };
type FloatingPanelGeometry = {
    x: number;
    y: number;
    width: number;
    height: number;
};

function sameExtensionPanel(
    left: ActiveExtensionPanel | null,
    right: ActiveExtensionPanel | null,
): boolean {
    if (!left || !right) return left === right;
    return left.extensionId === right.extensionId && left.panelId === right.panelId;
}

function defaultFloatingPanel(panelWidth: number): FloatingPanelGeometry {
    const viewportWidth = typeof window === "undefined" ? 1280 : window.innerWidth;
    const viewportHeight = typeof window === "undefined" ? 800 : window.innerHeight;
    const width = Math.min(
        Math.max(panelWidth, 360),
        Math.max(FLOATING_PANEL_MIN_WIDTH, viewportWidth - 96),
        FLOATING_PANEL_MAX_WIDTH,
    );
    const height = Math.min(
        640,
        Math.max(FLOATING_PANEL_MIN_HEIGHT, viewportHeight - 96),
        FLOATING_PANEL_MAX_HEIGHT,
    );
    return {
        x: Math.max(52, Math.min(96, viewportWidth - width - 24)),
        y: 56,
        width,
        height,
    };
}

function clampFloatingPanelGeometry<T extends FloatingPanelGeometry>(
    panel: T,
): T {
    const viewportWidth = typeof window === "undefined" ? 1280 : window.innerWidth;
    const viewportHeight = typeof window === "undefined" ? 800 : window.innerHeight;
    const maxWidth = Math.min(FLOATING_PANEL_MAX_WIDTH, viewportWidth - 24);
    const maxHeight = Math.min(FLOATING_PANEL_MAX_HEIGHT, viewportHeight - 24);
    const width = Math.min(Math.max(panel.width, FLOATING_PANEL_MIN_WIDTH), maxWidth);
    const height = Math.min(Math.max(panel.height, FLOATING_PANEL_MIN_HEIGHT), maxHeight);
    return {
        ...panel,
        width,
        height,
        x: Math.min(Math.max(12, panel.x), Math.max(12, viewportWidth - width - 12)),
        y: Math.min(Math.max(12, panel.y), Math.max(12, viewportHeight - height - 12)),
    };
}
/* ── Activity item definition ── */

export interface ActivityItem {
    id: SidebarView;
    icon?: ReactNode;
    iconSlot?: ProductIconSlotId;
    label: string;
    labelKey?: string;
}

/** Default activity items — all sidebar views */
const defaultActivityItems: ActivityItem[] = [
    {
        id: "explorer",
        iconSlot: "sidebar.explorer",
        label: "Explorer",
        labelKey: "sidebar.explorer",
    },
    {
        id: "toc",
        iconSlot: "sidebar.toc",
        label: "Table of Contents",
        labelKey: "sidebar.toc",
    },
    {
        id: "search",
        iconSlot: "sidebar.search",
        label: "Search",
        labelKey: "sidebar.search",
    },
    {
        id: "publish",
        iconSlot: "sidebar.publish",
        label: "Publish",
        labelKey: "sidebar.publish",
    },
    {
        id: "engine",
        iconSlot: "sidebar.engine",
        label: "Engine",
        labelKey: "sidebar.engine",
    },
];

/* ── Panel content map ── */

const panelMap: Record<SidebarView, React.ComponentType | null> = {
    files: OpenFilesSection,
    explorer: ExplorerPanel,
    search: SearchPanel,
    toc: TocPanel,
    plugins: null,
    engine: EnginePanel,
    extension: null,
    ai: null,
    publish: PublishPanel,
    settings: null,
};

function pinnedUnitTitle(unit: WorkspacePinnedUnit): string {
    return unit.label || unit.unitId;
}

function renderPinnedUnitActivityIcon(unit: WorkspacePinnedUnit): ReactNode {
    const icon = pluginUIRegistry.get(unit.pluginName)?.manifest.icon;
    if (typeof icon === "string" && icon.trim().length > 0) {
        return (
            <span aria-hidden="true" className="text-[15px] leading-none">
                {icon}
            </span>
        );
    }
    return <Puzzle size={17} aria-hidden="true" />;
}

/* ── Main Sidebar ── */

export interface SidebarProps {
    /** Custom activity items — if omitted, all views are shown. */
    activityItems?: ActivityItem[];
    /** Extra content in the bottom of the activity bar (e.g. Settings button) */
    bottomSlot?: React.ReactNode;
    /** Override panel components for specific sidebar views */
    panelOverrides?: Partial<Record<SidebarView, React.ComponentType>>;
}

export const Sidebar = memo(function Sidebar({ activityItems, bottomSlot, panelOverrides }: SidebarProps) {
    const t = useT("editor");
    const sidebarOpen = useEditorStore((s) => s.sidebarOpen);
    const sidebarView = useEditorStore((s) => s.sidebarView);
    const setSidebarView = useEditorStore((s) => s.setSidebarView);
    const pinnedUnits = useEditorStore((s) => s.pinnedUnits);
    const activePinnedUnitId = useEditorStore((s) => s.activePinnedUnitId);
    const showPinnedUnit = useEditorStore((s) => s.showPinnedUnit);
    const closePinnedUnitPanel = useEditorStore((s) => s.closePinnedUnitPanel);
    const removePinnedUnit = useEditorStore((s) => s.removePinnedUnit);
    const floatPinnedUnit = useEditorStore((s) => s.floatPinnedUnit);
    const dockPinnedUnit = useEditorStore((s) => s.dockPinnedUnit);
    const closeFloatingPinnedUnit = useEditorStore((s) => s.closeFloatingPinnedUnit);
    const moveFloatingPinnedUnit = useEditorStore((s) => s.moveFloatingPinnedUnit);
    const resizeFloatingPinnedUnit = useEditorStore((s) => s.resizeFloatingPinnedUnit);
    const hasDocumentContent = useEditorStore((s) => {
        const focusedTabId =
            s.panes.find((pane) => pane.paneId === s.focusedPaneId)?.tabId ??
            s.activeTabId;
        const focusedTab = s.tabs.find((tab) => tab.id === focusedTabId);
        return (focusedTab?.content?.trim().length ?? 0) > 0;
    });
    const hasOpenFileOrWorkspace = useEditorStore(
        (s) => s.tabs.length > 0 || s.workspaceFolders.length > 0,
    );

    const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
    const isDragging = useRef(false);
    const startX = useRef(0);
    const startWidth = useRef(0);
    const sidebarPresentation = getSidebarPresentation(sidebarView);
    const setActiveExtensionSidebarPanel = useExtensionHostStore(
        (s) => s.setActiveSidebarPanel,
    );
    const externalActiveExtPanel = useExtensionHostStore((s) => s.activeSidebarPanel);
    const floatingExtPanel = useExtensionHostStore((s) => s.floatingSidebarPanel);
    const floatSidebarPanel = useExtensionHostStore((s) => s.floatSidebarPanel);
    const dockFloatingSidebarPanel = useExtensionHostStore(
        (s) => s.dockFloatingSidebarPanel,
    );
    const closeFloatingSidebarPanel = useExtensionHostStore(
        (s) => s.closeFloatingSidebarPanel,
    );
    const moveFloatingSidebarPanel = useExtensionHostStore(
        (s) => s.moveFloatingSidebarPanel,
    );
    const resizeFloatingSidebarPanel = useExtensionHostStore(
        (s) => s.resizeFloatingSidebarPanel,
    );

    /* Extension-contributed sidebar panels live alongside the built-in views.
       They use the internal "extension" sidebar view so they do not piggyback
       on Engine or Settings state. */
    const extensionPanels = useExtensionSidebarPanels();
    const [activeExtPanel, setActiveExtPanel] =
        useState<ActiveExtensionPanel | null>(null);
    const activePinnedUnit = useMemo(
        () => pinnedUnits.find((unit) => unit.id === activePinnedUnitId) ?? null,
        [activePinnedUnitId, pinnedUnits],
    );
    const floatingPinnedUnits = useMemo(
        () => pinnedUnits.filter((unit) => unit.view?.mode === "floating"),
        [pinnedUnits],
    );
    const allowExtensionSidebarViewChange = useRef(false);
    const activityMenu = useContextMenu();
    const [activityMenuItems, setActivityMenuItems] = useState<ContextMenuItem[]>([]);
    const canOpenExtensionPanelWindow = Boolean(
        getPlatformService().supportsExtensionPanelWindows?.(),
    );

    const openActivityTraceMenu = useCallback(
        (event: React.MouseEvent, extraItems: ContextMenuItem[] = []) => {
            const traceItem = buildTraceRecordingMenuItem(t);
            const menuItems =
                extraItems.length > 0
                    ? [
                          traceItem,
                          { ...extraItems[0], separator: true },
                          ...extraItems.slice(1),
                      ]
                    : [traceItem];
            setActivityMenuItems(menuItems);
            activityMenu.handler(event);
        },
        [activityMenu, t],
    );

    const extensionPanelExists = useCallback(
        (panel: ActiveExtensionPanel | null) =>
            !!panel &&
            extensionPanels.some(
                (p) =>
                    p.extensionId === panel.extensionId &&
                    p.panel.id === panel.panelId,
            ),
        [extensionPanels],
    );

    const showExtensionPanel = useCallback(
        (panel: ActiveExtensionPanel) => {
            closePinnedUnitPanel();
            closeFloatingSidebarPanel();
            setActiveExtPanel(panel);
            setActiveExtensionSidebarPanel(panel);
            const { sidebarView: currentView } = useEditorStore.getState();
            const nextView: SidebarView = "extension";
            allowExtensionSidebarViewChange.current = nextView !== currentView;
            useEditorStore.setState({
                sidebarOpen: true,
                sidebarView: nextView,
            });
        },
        [closeFloatingSidebarPanel, closePinnedUnitPanel, setActiveExtensionSidebarPanel],
    );

    const detachExtensionPanel = useCallback(
        (panel: ActiveExtensionPanel) => {
            const geometry = defaultFloatingPanel(panelWidth);
            floatSidebarPanel({ ...panel, ...geometry });
            setActiveExtPanel(null);
            useEditorStore.setState({ sidebarOpen: false });
        },
        [floatSidebarPanel, panelWidth],
    );

    const openExtensionPanelWindow = useCallback(
        (
            panel: ActiveExtensionPanel,
            geometry: Pick<FloatingExtensionSidebarPanel, "width" | "height"> =
                defaultFloatingPanel(panelWidth),
        ): boolean => {
            const title =
                extensionPanels.find(
                    (entry) =>
                        entry.extensionId === panel.extensionId &&
                        entry.panel.id === panel.panelId,
                )?.panel.title ?? panel.panelId;
            try {
                const opened = getPlatformService().openExtensionSidebarPanel?.({
                    kind: "sidebar",
                    instanceId: `sidebar:${panel.extensionId}:${panel.panelId}:${Date.now().toString(36)}`,
                    extensionId: panel.extensionId,
                    panelId: panel.panelId,
                    title,
                    width: geometry.width,
                    height: geometry.height,
                });
                if (opened) {
                    setActiveExtPanel(null);
                    setActiveExtensionSidebarPanel(null);
                    useEditorStore.setState({ sidebarOpen: false });
                    return true;
                }
            } catch {
                // Keep the docked panel open if the native host rejects the request.
            }
            return false;
        },
        [
            extensionPanels,
            panelWidth,
            setActiveExtensionSidebarPanel,
        ],
    );

    const dockFloatingPanel = useCallback(() => {
        const panel = dockFloatingSidebarPanel();
        if (!panel) return;
        setActiveExtPanel(panel);
        const { sidebarView: currentView } = useEditorStore.getState();
        const nextView: SidebarView = "extension";
        allowExtensionSidebarViewChange.current = nextView !== currentView;
        useEditorStore.setState({
            sidebarOpen: true,
            sidebarView: nextView,
        });
    }, [dockFloatingSidebarPanel]);

    const buildPinnedUnitContextMenuItems = useCallback(
        (unit: WorkspacePinnedUnit): ContextMenuItem[] => {
            const isFloating = unit.view?.mode === "floating";
            return [
                {
                    key: `pinned-unit-open:${unit.id}`,
                    label: t("sidebar.pinnedUnit.openSource"),
                    icon: <FileText size={13} />,
                    onClick: () => {
                        void useEditorStore.getState().openFileFromPath(unit.documentPath);
                    },
                },
                {
                    key: `pinned-unit-float:${unit.id}`,
                    label: isFloating
                        ? t("sidebar.pinnedUnit.dock")
                        : t("sidebar.pinnedUnit.float"),
                    icon: isFloating ? <PanelLeft size={13} /> : <Maximize2 size={13} />,
                    onClick: () => {
                        if (isFloating) {
                            dockPinnedUnit(unit.id);
                        } else {
                            floatPinnedUnit(unit.id, defaultFloatingPanel(panelWidth));
                        }
                    },
                },
                {
                    key: `pinned-unit-remove:${unit.id}`,
                    label: t("sidebar.pinnedUnit.remove"),
                    icon: <PinOff size={13} />,
                    onClick: () => removePinnedUnit(unit.id),
                },
            ];
        },
        [dockPinnedUnit, floatPinnedUnit, panelWidth, removePinnedUnit, t],
    );

    const clearActiveExtensionPanel = useCallback(() => {
        setActiveExtPanel(null);
        setActiveExtensionSidebarPanel(null);
    }, [setActiveExtensionSidebarPanel]);

    // Drop remembered extension panels if their contributions disappear.
    useEffect(() => {
        const nextActivePanel =
            activeExtPanel ??
            (floatingExtPanel
                ? {
                      extensionId: floatingExtPanel.extensionId,
                      panelId: floatingExtPanel.panelId,
                  }
                : null);
        if (!sameExtensionPanel(externalActiveExtPanel, nextActivePanel)) {
            setActiveExtensionSidebarPanel(nextActivePanel);
        }
        if (activeExtPanel && !extensionPanelExists(activeExtPanel)) {
            setActiveExtPanel(null);
        }
        if (floatingExtPanel && !extensionPanelExists(floatingExtPanel)) {
            closeFloatingSidebarPanel();
        }
    }, [
        activeExtPanel,
        closeFloatingSidebarPanel,
        extensionPanelExists,
        externalActiveExtPanel,
        floatingExtPanel,
        setActiveExtensionSidebarPanel,
    ]);

    useEffect(() => {
        if (!externalActiveExtPanel && activeExtPanel) {
            setActiveExtPanel(null);
        }
    }, [activeExtPanel, externalActiveExtPanel]);

    useEffect(() => {
        if (activePinnedUnitId && !activePinnedUnit) {
            closePinnedUnitPanel();
        }
    }, [activePinnedUnit, activePinnedUnitId, closePinnedUnitPanel]);

    // Honor `host.focusPanel(panelId)` requests routed through the host store.
    useEffect(() => {
        return useExtensionHostStore.subscribe((state) => {
            const panelId = state.focusedPanelId;
            if (!panelId) return;
            // Consume the one-shot focus request before mutating any other host
            // state. Otherwise activeSidebarPanel changes can re-enter this
            // subscriber while focusedPanelId is still set and keep refocusing
            // the extension panel when the user tries to switch sidebars.
            useExtensionHostStore.setState({ focusedPanelId: null });
            const match = extensionPanels.find((p) => p.panel.id === panelId);
            if (match) {
                if (
                    floatingExtPanel?.extensionId === match.extensionId &&
                    floatingExtPanel.panelId === match.panel.id
                ) {
                    useExtensionHostStore.getState().setActiveSidebarPanel({
                        extensionId: match.extensionId,
                        panelId: match.panel.id,
                    });
                    return;
                }
                setActiveExtPanel({
                    extensionId: match.extensionId,
                    panelId: match.panel.id,
                });
                useExtensionHostStore.getState().setActiveSidebarPanel({
                    extensionId: match.extensionId,
                    panelId: match.panel.id,
                });
                const { sidebarView: currentView } = useEditorStore.getState();
                const nextView: SidebarView = "extension";
                allowExtensionSidebarViewChange.current = nextView !== currentView;
                useEditorStore.setState({
                    sidebarOpen: true,
                    sidebarView: nextView,
                });
            }
        });
    }, [extensionPanels, floatingExtPanel]);

    const previousSidebarView = useRef(sidebarView);
    useEffect(() => {
        if (previousSidebarView.current !== sidebarView && activeExtPanel) {
            if (allowExtensionSidebarViewChange.current) {
                allowExtensionSidebarViewChange.current = false;
            } else {
                clearActiveExtensionPanel();
            }
        }
        previousSidebarView.current = sidebarView;
    }, [activeExtPanel, clearActiveExtensionPanel, sidebarView]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        isDragging.current = true;
        startX.current = e.clientX;
        startWidth.current = panelWidth;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
    }, [panelWidth]);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging.current) return;
            const delta = e.clientX - startX.current;
            const newWidth = Math.min(
                MAX_PANEL_WIDTH,
                Math.max(MIN_PANEL_WIDTH, startWidth.current + delta),
            );
            setPanelWidth(newWidth);
        };
        const handleMouseUp = () => {
            if (!isDragging.current) return;
            isDragging.current = false;
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, []);

    const visibleViews = useMemo(() => {
        const next = new Set<SidebarView>([
            "explorer",
            "engine",
            "settings",
        ]);
        if (hasOpenFileOrWorkspace) {
            next.add("search");
            next.add("publish");
        }
        if (hasDocumentContent) {
            next.add("toc");
        }
        return next;
    }, [hasDocumentContent, hasOpenFileOrWorkspace]);

    const items = useMemo(
        () =>
            (activityItems ?? defaultActivityItems).filter((item) =>
                visibleViews.has(item.id),
            ),
        [activityItems, visibleViews],
    );
    const PanelComponent = panelOverrides?.[sidebarView] ?? panelMap[sidebarView] ?? null;

    useEffect(() => {
        if (!sidebarOpen || sidebarView === "settings") return;
        if (activePinnedUnit) return;
        if (sidebarView === "extension") {
            if (activeExtPanel) return;
            useEditorStore.setState({ sidebarOpen: false });
            return;
        }
        if (visibleViews.has(sidebarView)) return;
        const fallback = items[0]?.id;
        if (fallback) {
            setSidebarView(fallback);
            return;
        }
        useEditorStore.setState({ sidebarOpen: false });
    }, [activeExtPanel, activePinnedUnit, items, setSidebarView, sidebarOpen, sidebarView, visibleViews]);

    return (
        <div data-testid="sidebar" className="flex shrink-0 h-full">
            {/* Activity bar — always visible */}
            <div className="flex flex-col items-center w-10 border-r border-border bg-muted/30 py-1.5 gap-0.5">
                {items.map((item) => {
                    const label = item.labelKey ? t(item.labelKey) : item.label;
                    return (
                        <button
                            type="button"
                            key={item.id}
                            title={label}
                            onContextMenu={(event) => openActivityTraceMenu(event)}
                            onClick={() => {
                                if (activePinnedUnit) {
                                    closePinnedUnitPanel();
                                    setSidebarView(item.id);
                                    return;
                                }
                                if (!activeExtPanel && sidebarOpen && sidebarView === item.id) {
                                    useEditorStore.setState({ sidebarOpen: false });
                                    return;
                                }
                                if (activeExtPanel) {
                                    clearActiveExtensionPanel();
                                    useEditorStore.setState({
                                        sidebarOpen: true,
                                        sidebarView: item.id,
                                    });
                                } else {
                                    setSidebarView(item.id);
                                }
                            }}
                            className={cn(
                                "flex items-center justify-center w-8 h-8 rounded-md transition-colors",
                                sidebarOpen &&
                                    !activeExtPanel &&
                                    !activePinnedUnit &&
                                    sidebarView === item.id
                                    ? "text-primary bg-primary/10"
                                    : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
                            )}
                        >
                            {item.icon ??
                                (item.iconSlot ? (
                                    <ProductIcon
                                        slot={item.iconSlot}
                                        size={18}
                                    />
                                ) : null)}
                        </button>
                    );
                })}

                {/* Extension-contributed activity items */}
                {extensionPanels.length > 0 ? (
                    <div className="mt-1 flex w-full flex-col items-center gap-0.5 pt-1">
                        <div
                            aria-hidden="true"
                            data-extension-activity-divider
                            className="mb-0.5 h-px w-5 bg-border"
                        />
                        {extensionPanels.map(({ extensionId, panel }) => {
                            const isDockedActive =
                                activeExtPanel?.extensionId === extensionId &&
                                activeExtPanel?.panelId === panel.id;
                            const isFloatingActive =
                                floatingExtPanel?.extensionId === extensionId &&
                                floatingExtPanel?.panelId === panel.id;
                            return (
                                <button
                                    type="button"
                                    key={`${extensionId}:${panel.id}`}
                                    title={panel.title}
                                    onContextMenu={(event) => {
                                        const items = buildExtensionContextMenuItems(
                                            { surface: "sidebar-item" },
                                            { firstSeparator: false },
                                        );
                                        openActivityTraceMenu(event, items);
                                    }}
                                    onClick={() => {
                                        if (isDockedActive && sidebarOpen) {
                                            useEditorStore.setState({ sidebarOpen: false });
                                            setActiveExtPanel(null);
                                            setActiveExtensionSidebarPanel(null);
                                            return;
                                        }
                                        showExtensionPanel({
                                            extensionId,
                                            panelId: panel.id,
                                        });
                                    }}
                                    className={cn(
                                        "flex items-center justify-center w-8 h-8 rounded-md transition-colors",
                                        (sidebarOpen && isDockedActive) || isFloatingActive
                                            ? "text-primary bg-primary/10"
                                            : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
                                    )}
                                >
                                    {renderExtensionActivityIcon(
                                        extensionId,
                                        panel.icon,
                                    )}
                                </button>
                            );
                        })}
                    </div>
                ) : null}

                {pinnedUnits.length > 0 ? (
                    <div className="mt-1 flex w-full flex-col items-center gap-0.5 pt-1">
                        <div
                            aria-hidden="true"
                            data-pinned-unit-activity-divider
                            className="mb-0.5 h-px w-5 bg-border"
                        />
                        {pinnedUnits.map((unit) => {
                            const isDockedActive =
                                activePinnedUnit?.id === unit.id && sidebarOpen;
                            const isFloatingActive = unit.view?.mode === "floating";
                            return (
                                <button
                                    type="button"
                                    key={unit.id}
                                    title={`${pinnedUnitTitle(unit)} (${unit.pluginName})`}
                                    onContextMenu={(event) => {
                                        openActivityTraceMenu(
                                            event,
                                            buildPinnedUnitContextMenuItems(unit),
                                        );
                                    }}
                                    onClick={() => {
                                        if (isDockedActive) {
                                            closePinnedUnitPanel();
                                            useEditorStore.setState({ sidebarOpen: false });
                                            return;
                                        }
                                        if (activeExtPanel) {
                                            setActiveExtPanel(null);
                                            setActiveExtensionSidebarPanel(null);
                                        }
                                        if (isFloatingActive) {
                                            dockPinnedUnit(unit.id);
                                            return;
                                        }
                                        showPinnedUnit(unit.id);
                                    }}
                                    className={cn(
                                        "flex items-center justify-center w-8 h-8 rounded-md transition-colors",
                                        isDockedActive || isFloatingActive
                                            ? "text-primary bg-primary/10"
                                            : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
                                    )}
                                >
                                    {renderPinnedUnitActivityIcon(unit)}
                                </button>
                            );
                        })}
                    </div>
                ) : null}

                <div className="flex-1" />

                {bottomSlot ?? (
                    <button
                        type="button"
                        title={t("sidebar.settings")}
                        onContextMenu={(event) => openActivityTraceMenu(event)}
                        onClick={() => {
                            if (activePinnedUnit) {
                                closePinnedUnitPanel();
                                useEditorStore.getState().setSidebarView("settings");
                                return;
                            }
                            if (!activeExtPanel && sidebarOpen && sidebarView === "settings") {
                                useEditorStore.setState({ sidebarOpen: false });
                                return;
                            }
                            if (activeExtPanel) {
                                clearActiveExtensionPanel();
                                useEditorStore.setState({
                                    sidebarOpen: true,
                                    sidebarView: "settings",
                                });
                            } else {
                                useEditorStore.getState().setSidebarView("settings");
                            }
                        }}
                        className={cn(
                            "flex items-center justify-center w-8 h-8 rounded-md transition-colors",
                            sidebarOpen &&
                                !activeExtPanel &&
                                !activePinnedUnit &&
                                sidebarView === "settings"
                                ? "text-primary bg-primary/10"
                                : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
                        )}
                    >
                        <Settings size={18} />
                    </button>
                )}
            </div>

            {/* Panel content — shown when sidebar is open */}
            {sidebarOpen && (activeExtPanel || activePinnedUnit || sidebarPresentation === "docked") ? (
                <div
                    className="border-r border-border bg-muted/10 flex flex-col min-h-0 relative"
                    style={{
                        width: `min(${panelWidth}px, calc(100vw - 120px))`,
                    }}
                >
                    {activeExtPanel ? (
                        <ExtensionDockedPanel
                            panel={activeExtPanel}
                            title={
                                extensionPanels.find(
                                    (entry) =>
                                        entry.extensionId === activeExtPanel.extensionId &&
                                        entry.panel.id === activeExtPanel.panelId,
                                )?.panel.title ?? activeExtPanel.panelId
                            }
                            onFloat={() => detachExtensionPanel(activeExtPanel)}
                            onOpenWindow={
                                canOpenExtensionPanelWindow
                                    ? () => openExtensionPanelWindow(activeExtPanel)
                                    : undefined
                            }
                            onClose={() => {
                                useEditorStore.setState({ sidebarOpen: false });
                                setActiveExtPanel(null);
                                setActiveExtensionSidebarPanel(null);
                            }}
                        />
                    ) : activePinnedUnit ? (
                        <PinnedUnitDockedPanel
                            unit={activePinnedUnit}
                            onFloat={() => {
                                floatPinnedUnit(
                                    activePinnedUnit.id,
                                    defaultFloatingPanel(panelWidth),
                                );
                            }}
                            onClose={() => {
                                closePinnedUnitPanel();
                                useEditorStore.setState({ sidebarOpen: false });
                            }}
                        />
                    ) : PanelComponent ? (
                        <PanelComponent />
                    ) : null}
                    {/* Resize handle */}
                    <div
                        onMouseDown={handleMouseDown}
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/30 active:bg-primary/50 transition-colors z-10"
                    />
                </div>
            ) : null}

            {/* Snapshot preview column — right of sidebar panel */}
            <SnapshotPreviewColumn />
            {floatingExtPanel ? (
                <FloatingExtensionPanel
                    panel={floatingExtPanel}
                    title={
                        extensionPanels.find(
                            (entry) =>
                                entry.extensionId === floatingExtPanel.extensionId &&
                                entry.panel.id === floatingExtPanel.panelId,
                        )?.panel.title ?? floatingExtPanel.panelId
                    }
                    onMove={moveFloatingSidebarPanel}
                    onResize={resizeFloatingSidebarPanel}
                    onDock={dockFloatingPanel}
                    onOpenWindow={
                        canOpenExtensionPanelWindow
                            ? () => {
                                  if (
                                      openExtensionPanelWindow(floatingExtPanel, {
                                          width: floatingExtPanel.width,
                                          height: floatingExtPanel.height,
                                      })
                                  ) {
                                      closeFloatingSidebarPanel();
                                  }
                              }
                            : undefined
                    }
                    onClose={closeFloatingSidebarPanel}
                />
            ) : null}
            {floatingPinnedUnits.map((unit) => (
                <FloatingPinnedUnitPanel
                    key={unit.id}
                    unit={unit}
                    onMove={(x, y) => moveFloatingPinnedUnit(unit.id, x, y)}
                    onResize={(width, height) =>
                        resizeFloatingPinnedUnit(unit.id, width, height)
                    }
                    onDock={() => dockPinnedUnit(unit.id)}
                    onClose={() => closeFloatingPinnedUnit(unit.id)}
                />
            ))}
            <ContextMenu
                {...activityMenu.props}
                items={activityMenuItems}
            />
        </div>
    );
});

function ExtensionDockedPanel({
    panel,
    title,
    onFloat,
    onOpenWindow,
    onClose,
}: {
    panel: ActiveExtensionPanel;
    title: string;
    onFloat: () => void;
    onOpenWindow?: () => void;
    onClose: () => void;
}) {
    const actions = useExtensionSidebarPanelHeaderActions(panel);
    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex h-8 shrink-0 items-center gap-1 border-b border-border bg-muted/20 px-2">
                <span className="min-w-0 flex-1 truncate text-[11px] font-medium">
                    {title}
                </span>
                {actions}
                <button
                    type="button"
                    className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                    onClick={onFloat}
                    title="Float panel"
                >
                    <Maximize2 size={13} />
                </button>
                {onOpenWindow ? (
                    <button
                        type="button"
                        className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                        onClick={onOpenWindow}
                        title="Open panel in separate window"
                    >
                        <ExternalLink size={13} />
                    </button>
                ) : null}
                <SidebarCloseButton onClick={onClose} />
            </div>
            <div className="min-h-0 flex-1">
                <ExtensionSidebarPanelView
                    extensionId={panel.extensionId}
                    panelId={panel.panelId}
                />
            </div>
        </div>
    );
}

function PinnedUnitDockedPanel({
    unit,
    onFloat,
    onClose,
}: {
    unit: WorkspacePinnedUnit;
    onFloat: () => void;
    onClose: () => void;
}) {
    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex h-8 shrink-0 items-center gap-1 border-b border-border bg-muted/20 px-2">
                <span className="mr-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                    {renderPinnedUnitActivityIcon(unit)}
                </span>
                <span className="min-w-0 flex-1 truncate text-[11px] font-medium">
                    {pinnedUnitTitle(unit)}
                </span>
                <button
                    type="button"
                    className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                    onClick={onFloat}
                    title="Float panel"
                >
                    <Maximize2 size={13} />
                </button>
                <SidebarCloseButton onClick={onClose} />
            </div>
            <div className="min-h-0 flex-1">
                <PinnedUnitPanel unit={unit} />
            </div>
        </div>
    );
}

function FloatingPinnedUnitPanel({
    unit,
    onMove,
    onResize,
    onDock,
    onClose,
}: {
    unit: WorkspacePinnedUnit;
    onMove: (x: number, y: number) => void;
    onResize: (width: number, height: number) => void;
    onDock: () => void;
    onClose: () => void;
}) {
    const dragRef = useRef<{
        startX: number;
        startY: number;
        x: number;
        y: number;
    } | null>(null);
    const resizeRef = useRef<{
        startX: number;
        startY: number;
        width: number;
        height: number;
    } | null>(null);
    const geometry = {
        ...defaultFloatingPanel(DEFAULT_PANEL_WIDTH),
        ...(unit.view?.mode === "floating" ? unit.view : {}),
    };
    const clamped = clampFloatingPanelGeometry(geometry);

    const startDrag = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (event.button !== 0) return;
            event.preventDefault();
            dragRef.current = {
                startX: event.clientX,
                startY: event.clientY,
                x: clamped.x,
                y: clamped.y,
            };
            document.body.style.userSelect = "none";
        },
        [clamped.x, clamped.y],
    );

    const startResize = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (event.button !== 0) return;
            event.preventDefault();
            event.stopPropagation();
            resizeRef.current = {
                startX: event.clientX,
                startY: event.clientY,
                width: clamped.width,
                height: clamped.height,
            };
            document.body.style.cursor = "nwse-resize";
            document.body.style.userSelect = "none";
        },
        [clamped.height, clamped.width],
    );

    useEffect(() => {
        const handleMouseMove = (event: MouseEvent) => {
            if (dragRef.current) {
                const next = clampFloatingPanelGeometry({
                    ...clamped,
                    x: dragRef.current.x + event.clientX - dragRef.current.startX,
                    y: dragRef.current.y + event.clientY - dragRef.current.startY,
                });
                onMove(next.x, next.y);
                return;
            }
            if (resizeRef.current) {
                const next = clampFloatingPanelGeometry({
                    ...clamped,
                    width:
                        resizeRef.current.width +
                        event.clientX -
                        resizeRef.current.startX,
                    height:
                        resizeRef.current.height +
                        event.clientY -
                        resizeRef.current.startY,
                });
                onResize(next.width, next.height);
            }
        };
        const handleMouseUp = () => {
            if (!dragRef.current && !resizeRef.current) return;
            dragRef.current = null;
            resizeRef.current = null;
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [clamped, onMove, onResize]);

    return (
        <div
            className="fixed z-50 flex min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-background shadow-2xl"
            style={{
                left: clamped.x,
                top: clamped.y,
                width: clamped.width,
                height: clamped.height,
            }}
        >
            <div
                className="flex h-9 shrink-0 cursor-grab select-none items-center gap-1 border-b border-border bg-muted/30 px-2 active:cursor-grabbing"
                onMouseDown={startDrag}
            >
                <GripHorizontal size={14} className="shrink-0 text-muted-foreground" />
                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                    {renderPinnedUnitActivityIcon(unit)}
                </span>
                <span className="min-w-0 flex-1 truncate text-[11px] font-medium">
                    {pinnedUnitTitle(unit)}
                </span>
                <button
                    type="button"
                    className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={onDock}
                    title="Dock panel"
                >
                    <PanelLeft size={13} />
                </button>
                <button
                    type="button"
                    className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={onClose}
                    title="Close panel"
                >
                    <X size={13} />
                </button>
            </div>
            <div className="min-h-0 flex-1">
                <PinnedUnitPanel unit={unit} />
            </div>
            <button
                type="button"
                className="absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize rounded-tl border-l border-t border-border bg-muted/40"
                onMouseDown={startResize}
                aria-label="Resize panel"
            />
        </div>
    );
}

function FloatingExtensionPanel({
    panel,
    title,
    onMove,
    onResize,
    onDock,
    onOpenWindow,
    onClose,
}: {
    panel: FloatingExtensionSidebarPanel;
    title: string;
    onMove: (x: number, y: number) => void;
    onResize: (width: number, height: number) => void;
    onDock: () => void;
    onOpenWindow?: () => void;
    onClose: () => void;
}) {
    const actions = useExtensionSidebarPanelHeaderActions(panel);
    const dragRef = useRef<{
        startX: number;
        startY: number;
        x: number;
        y: number;
    } | null>(null);
    const resizeRef = useRef<{
        startX: number;
        startY: number;
        width: number;
        height: number;
    } | null>(null);
    const clamped = clampFloatingPanelGeometry(panel);

    const startDrag = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (event.button !== 0) return;
            event.preventDefault();
            dragRef.current = {
                startX: event.clientX,
                startY: event.clientY,
                x: clamped.x,
                y: clamped.y,
            };
            document.body.style.userSelect = "none";
        },
        [clamped.x, clamped.y],
    );

    const startResize = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (event.button !== 0) return;
            event.preventDefault();
            event.stopPropagation();
            resizeRef.current = {
                startX: event.clientX,
                startY: event.clientY,
                width: clamped.width,
                height: clamped.height,
            };
            document.body.style.cursor = "nwse-resize";
            document.body.style.userSelect = "none";
        },
        [clamped.height, clamped.width],
    );

    useEffect(() => {
        const handleMouseMove = (event: MouseEvent) => {
            if (dragRef.current) {
                const next = clampFloatingPanelGeometry({
                    ...clamped,
                    x: dragRef.current.x + event.clientX - dragRef.current.startX,
                    y: dragRef.current.y + event.clientY - dragRef.current.startY,
                });
                onMove(next.x, next.y);
                return;
            }
            if (resizeRef.current) {
                const next = clampFloatingPanelGeometry({
                    ...clamped,
                    width:
                        resizeRef.current.width +
                        event.clientX -
                        resizeRef.current.startX,
                    height:
                        resizeRef.current.height +
                        event.clientY -
                        resizeRef.current.startY,
                });
                onResize(next.width, next.height);
            }
        };
        const handleMouseUp = () => {
            if (!dragRef.current && !resizeRef.current) return;
            dragRef.current = null;
            resizeRef.current = null;
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [clamped, onMove, onResize]);

    return (
        <div
            className="fixed z-50 flex min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-background shadow-2xl"
            style={{
                left: clamped.x,
                top: clamped.y,
                width: clamped.width,
                height: clamped.height,
            }}
        >
            <div
                className="flex h-9 shrink-0 cursor-grab select-none items-center gap-1 border-b border-border bg-muted/30 px-2 active:cursor-grabbing"
                onMouseDown={startDrag}
            >
                <GripHorizontal size={14} className="shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-[11px] font-medium">
                    {title}
                </span>
                {actions}
                {onOpenWindow ? (
                    <button
                        type="button"
                        className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={onOpenWindow}
                        title="Open panel in separate window"
                    >
                        <ExternalLink size={13} />
                    </button>
                ) : null}
                <button
                    type="button"
                    className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={onDock}
                    title="Dock panel"
                >
                    <PanelLeft size={13} />
                </button>
                <button
                    type="button"
                    className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={onClose}
                    title="Close panel"
                >
                    <X size={13} />
                </button>
            </div>
            <div className="min-h-0 flex-1">
                <ExtensionSidebarPanelView
                    extensionId={panel.extensionId}
                    panelId={panel.panelId}
                />
            </div>
            <button
                type="button"
                className="absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize rounded-tl border-l border-t border-border bg-muted/40"
                onMouseDown={startResize}
                aria-label="Resize panel"
            />
        </div>
    );
}

function useExtensionSidebarPanelHeaderActions(
    panel: ActiveExtensionPanel,
): ReactNode {
    const panels = useExtensionSidebarPanels();
    const props = useExtensionUIProps(panel.extensionId);
    const entry = panels.find(
        (item) =>
            item.extensionId === panel.extensionId &&
            item.panel.id === panel.panelId,
    );
    if (!entry?.panel.headerActions || !props) return null;
    return entry.panel.headerActions(props);
}
